import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import errorBoundary from '@ifeng-fe/errorBoundary';
import { MAP_VIEWBOX, STATE_PATHS } from '../../../../common/midterm/statePaths';
import { STATE_NAMES } from '../../../../common/midterm/constants';
import { getOutcome } from '../States/result';
import styles from './index.css';

/** 开票数据用「得克萨斯州」，地图州名表用「德克萨斯州」。 */
const NAME_ALIAS = {
    得克萨斯州: '德克萨斯州',
};

const ABSENT = '#EBEBEB';

const LEGEND_IMAGE = [
    'https://x0.ifengimg.com/ucms/2026_44/3A2CFCF49E66A45BF9C64A919B6C1BD37DAC8C8B_size10_w945_h132.png',
    'https://x0.ifengimg.com/ucms/2026_44/7709E1804B58062370B0E5D4505CAB83EB530B97_size8_w945_h123.png',
    'https://x0.ifengimg.com/ucms/2026_44/7709E1804B58062370B0E5D4505CAB83EB530B97_size8_w945_h123.png',
];

const STATUS_CLASS = {
    '计票中...': 'counting',
    民主党获胜: 'demWin',
    共和党获胜: 'gopWin',
    其他获胜: 'otherWin',
};

const mapColor = (state, byCount) => {
    if (!state) return ABSENT;
    return getOutcome(state, { byCount }).mapFill;
};

const Popup = React.forwardRef(({ name, state, byCount = false, onClose, place }, ref) => {
    const outcome = state ? getOutcome(state, { byCount }) : null;
    const label = outcome ? outcome.label : '未参选';
    const statusClass = styles[STATUS_CLASS[label]] || '';
    const dem = state ? Number(state.dem) || 0 : 0;
    const gop = state ? Number(state.gop) || 0 : 0;
    const oth = state ? Number(state.oth) || 0 : 0;

    return (
        <div
            ref={ref}
            className={styles.popup}
            style={{
                top: place ? place.top : 0,
                left: place ? place.left : 0,
                maxWidth: place ? place.maxWidth : undefined,
                visibility: place ? 'visible' : 'hidden',
            }}
            onClick={event => event.stopPropagation()}
        >
            <div className={styles.popupHead}>
                <div className={styles.popupTitle}>
                    <span>{name}</span>
                    <span className={statusClass}>｜{label}</span>
                </div>
                <div className={styles.close} onClick={onClose}>
                    <svg viewBox="0 0 20 20" aria-hidden="true">
                        <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                </div>
            </div>
            <div className={styles.track}>
                <div className={styles.gray} style={{ backgroundColor: outcome ? outcome.gray : ABSENT }} />
                {outcome && outcome.demWidth > 0 ? <div className={styles.demBar} style={{ width: `${outcome.demWidth}%`, backgroundColor: outcome.demFill }} /> : null}
                {outcome && outcome.othWidth > 0 ? (
                    <div className={styles.othBar} style={{ left: `${outcome.demWidth}%`, width: `${outcome.othWidth}%`, backgroundColor: outcome.othFill }} />
                ) : null}
                {outcome && outcome.gopWidth > 0 ? <div className={styles.gopBar} style={{ width: `${outcome.gopWidth}%`, backgroundColor: outcome.gopFill }} /> : null}
                {outcome && outcome.showCount && dem > 0 ? <span className={styles.countLeft}>{dem}</span> : null}
                {outcome && outcome.showCount && oth > 0 ? (
                    <span className={styles.countMid} style={{ left: `${outcome.demWidth + outcome.othWidth / 2}%` }}>
                        {oth}
                    </span>
                ) : null}
                {outcome && outcome.showCount && gop > 0 ? <span className={styles.countRight}>{gop}</span> : null}
            </div>
            {outcome && outcome.showMeta ? (
                <div className={styles.meta}>
                    <span className={styles.demText}>民主党</span>
                    <span className={styles.gopText}>共和党</span>
                </div>
            ) : null}
        </div>
    );
});

Popup.propTypes = {
    name: PropTypes.string,
    state: PropTypes.object,
    byCount: PropTypes.bool,
    onClose: PropTypes.func,
    place: PropTypes.shape({
        top: PropTypes.number,
        left: PropTypes.number,
        maxWidth: PropTypes.number,
    }),
};

const PAGE_GUTTER = 8;

const placePopup = (map, popup, path) => {
    const mapBox = map.getBoundingClientRect();
    const pathBox = path.getBoundingClientRect();
    const pageWidth = document.documentElement.clientWidth;
    const maxWidth = Math.max(0, pageWidth - PAGE_GUTTER * 2);
    popup.style.maxWidth = `${maxWidth}px`;
    const width = popup.getBoundingClientRect().width;
    let left = pathBox.left + pathBox.width / 2 - width / 2 - mapBox.left;
    const minLeft = PAGE_GUTTER - mapBox.left;
    const maxLeft = pageWidth - PAGE_GUTTER - width - mapBox.left;
    left = minLeft <= maxLeft ? Math.min(Math.max(left, minLeft), maxLeft) : minLeft;
    return {
        top: pathBox.bottom - mapBox.top + PAGE_GUTTER,
        left,
        maxWidth,
    };
};

const MapPanel = ({ type = 0, data = [], showLegend = true, interactive = true }) => {
    const [selected, setSelected] = useState(null);
    const [place, setPlace] = useState(null);
    const mapRef = useRef(null);
    const popupRef = useRef(null);
    const byName = useMemo(() => {
        const map = {};
        data.forEach(item => {
            map[item.name] = item;
            if (NAME_ALIAS[item.name]) map[NAME_ALIAS[item.name]] = item;
        });
        return map;
    }, [data]);

    useEffect(() => {
        setSelected(null);
        setPlace(null);
    }, [type]);

    useLayoutEffect(() => {
        if (!selected || !mapRef.current || !popupRef.current) return undefined;
        const update = () => {
            const path = mapRef.current.querySelector(`[data-abbr="${selected}"]`);
            if (!path) return;
            setPlace(placePopup(mapRef.current, popupRef.current, path));
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [selected]);

    const byCount = type === 1 || type === 2;
    const selectedState = selected ? byName[STATE_NAMES[selected]] || null : null;

    const handleSelect = abbr => {
        setPlace(null);
        setSelected(current => (current === abbr ? null : abbr));
    };

    return (
        <section className={`${showLegend && interactive ? styles.card : styles.plain} ${interactive && selected ? styles.cardOpen : ''}`}>
            {showLegend ? <img className={styles.legend} src={LEGEND_IMAGE[type] || LEGEND_IMAGE[0]} alt={['众议院图例', '参议院图例', '州长图例'][type] || '图例'} /> : null}
            <div className={styles.mapArea} ref={mapRef}>
                <svg className={styles.svg} viewBox={MAP_VIEWBOX} role="img" aria-label="美国选情地图">
                    {Object.keys(STATE_PATHS).map(abbr => {
                        const state = byName[STATE_NAMES[abbr]];
                        return (
                            <path
                                key={abbr}
                                d={STATE_PATHS[abbr]}
                                fill={mapColor(state, byCount)}
                                stroke="#ffffff"
                                strokeWidth={selected === abbr ? 2.4 : 1}
                                data-abbr={abbr}
                                // eslint-disable-next-line no-nested-ternary
                                className={!interactive ? styles.stateStatic : selected === abbr ? styles.stateActive : styles.state}
                                onClick={interactive ? () => handleSelect(abbr) : undefined}
                            >
                                <title>{STATE_NAMES[abbr] || abbr}</title>
                            </path>
                        );
                    })}
                </svg>
                {interactive && selected ? (
                    <Popup ref={popupRef} name={(selectedState && selectedState.name) || STATE_NAMES[selected] || selected} state={selectedState} byCount={byCount} place={place} onClose={() => setSelected(null)} />
                ) : null}
                {type === 1 ? <div className={styles.senateNote}>本图仅展示本次参议院改选席位的选举结果</div> : null}
            </div>
        </section>
    );
};

MapPanel.propTypes = {
    type: PropTypes.number,
    data: PropTypes.array,
    showLegend: PropTypes.bool,
    interactive: PropTypes.bool,
};

export default errorBoundary(MapPanel);
